import React, { useMemo, useState, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase/config";
import { EventsSectionHeader } from "./components/EventsSectionHeader";
import { UpcomingEventCard } from "./components/UpcomingEventCard";
import { PreviousEventCard } from "./components/PreviousEventCard";
import { YearFilterTabs } from "./components/YearFilterTabs";
import styles from "./EventsPage.module.css";
import {
  FALLBACK_YEAR_FILTER,
  pickWebsiteUrl,
  splitEventsByDate,
  formatEventDate,
  buildYearOptions,
  filterPastEventsByYear,
  EVENTS_COPY,
} from "./utils";



function EventsPage() {
  const [events, setEvents] = useState([]);
  const [yearFilter, setYearFilter] = useState(FALLBACK_YEAR_FILTER);
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "events"), (snapshot) => {
      setEvents(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  const { upcoming, past } = useMemo(() => splitEventsByDate(events), [events]);

  const yearOptions = useMemo(() => buildYearOptions(past), [past]);

  const filteredPast = useMemo(() => filterPastEventsByYear(past, yearFilter), [past, yearFilter]);

  return (
    <div className={styles.eventsRoot}>
      <div className={styles.container}>
        {upcoming?.length > 0 && (
          <section
            className={styles.sectionBlock}
            aria-labelledby="upcoming-events-heading"
          >
            <EventsSectionHeader
              headingId="upcoming-events-heading"
              title="Upcoming Events"
              description={EVENTS_COPY.upcoming}
              descriptionTone="upcoming"
            />
            <div className={styles.upcomingList}>
              {upcoming.length > 0 ? (
                upcoming.map((event) => (
                  <UpcomingEventCard
                    key={event.id}
                    title={event.title}
                    location={event.location}
                    dateLabel={formatEventDate(event.date, event.endDate)}
                    websiteUrl={pickWebsiteUrl(event)}
                  />
                ))
              ) : (
                <div className={styles.emptyState}>
                  <h3 className={styles.emptyStateTitle}>
                    {EVENTS_COPY.emptyUpcomingTitle}
                  </h3>
                  <p className={styles.emptyStateText}>
                    {EVENTS_COPY.emptyUpcomingText}
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        <section
          className={styles.sectionBlock}
          aria-labelledby="previous-events-heading"
        >
          <EventsSectionHeader
            headingId="previous-events-heading"
            title="Previous Events"
            description={EVENTS_COPY.previous}
            descriptionTone="previous"
          />
          <div className={styles.filterRow}>
            <YearFilterTabs
              options={yearOptions}
              activeValue={yearFilter}
              onChange={setYearFilter}
            />
            <div className={styles.filterSpacer} aria-hidden />
          </div>
          <div className={styles.pastGrid}>
            {filteredPast.length > 0 ? (
              filteredPast.map((event) => (
                <PreviousEventCard
                  key={event.id}
                  imageUrl={event.image}
                  title={event.title}
                  location={event.location}
                  dateLabel={formatEventDate(event.date, event.endDate)}
                />
              ))
            ) : (
              <p className={styles.emptyGrid}>
                {past.length === 0
                  ? EVENTS_COPY.emptyPreviousNoData
                  : EVENTS_COPY.emptyPreviousNoYear}
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default EventsPage;
